import { Head, router, useForm, usePage } from "@inertiajs/react"
import {
  Boxes,
  KeyRound,
  Mail,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem, SharedProps } from "@/types"

interface GrantRow {
  id: number
  scope_type: "workspace" | "facility"
  scope_id: number
  scope_label: string
  role: string
}
interface Person {
  user_id: number
  name: string
  email: string
  org_membership_id: number | null
  org_role: string | null
  verified: boolean
  has_signed_in: boolean
  invited_at: string | null
  grants: GrantRow[]
  // {feature => "deny" | "default"} — the deny-only feature-access lane. Absent
  // feature or "default" = inherit role (no restriction).
  feature_access: Record<string, string>
}

interface FeatureCatalogItem {
  key: string
  label: string
}

// Mirrors User#generates_token_for :invitation, expires_in: 14.days (user.rb).
const INVITE_TTL_DAYS = 14

type InviteState = "accepted" | "pending" | "expired"

function inviteState(p: Person): InviteState {
  if (p.verified || p.has_signed_in) return "accepted"
  if (!p.invited_at) return "pending"
  const ageDays =
    (Date.now() - new Date(p.invited_at).getTime()) / (1000 * 60 * 60 * 24)
  return ageDays > INVITE_TTL_DAYS ? "expired" : "pending"
}
interface WorkspaceMember {
  grant_id: number
  user_id: number
  name: string
  email: string
  role: string
}
interface WorkspaceRow {
  id: number
  name: string
  facility_ids: number[]
  members: WorkspaceMember[]
}
interface FacilityOption {
  id: number
  name: string
}

interface Props {
  people: Person[]
  workspaces: WorkspaceRow[]
  facilities: FacilityOption[]
  roles: string[]
  feature_catalog: FeatureCatalogItem[]
  current_user_id: number
}

type Tab = "people" | "workspaces"

export default function AccessIndex({
  people,
  workspaces,
  facilities,
  roles,
  feature_catalog,
  current_user_id,
}: Props) {
  const { organization } = usePage<SharedProps>().props
  const routes = useOrgRoutes()
  const [tab, setTab] = useState<Tab>("people")
  const [addOpen, setAddOpen] = useState(false)
  const [manageUserId, setManageUserId] = useState<number | null>(null)

  if (!organization) return null
  const org = organization

  // Re-derive from the live `people` prop so the manage dialog reflects grants
  // added/removed during the session (it stays open across Inertia reloads).
  const managePerson = people.find((p) => p.user_id === manageUserId) ?? null

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "All Facilities", href: "/" },
    { title: "Access", href: routes.accessPath() },
  ]

  function revokeGrant(p: Person, g: GrantRow) {
    if (!confirm(`Revoke ${p.email}'s access to ${g.scope_label}?`)) return
    router.delete(routes.accessGrantPath(g.id), { preserveScroll: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Access — ${org.name}`} />

      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <ShieldCheck className="h-6 w-6" /> Access
          </h1>
          <p className="text-muted-foreground text-sm">
            Who can access {org.name} — your staff (organization-wide) and
            customers or partners (scoped to specific facilities).
          </p>
        </div>

        {/* Tab switcher */}
        <div className="bg-muted inline-flex w-fit rounded-lg p-1">
          <TabButton active={tab === "people"} onClick={() => setTab("people")}>
            People
          </TabButton>
          <TabButton
            active={tab === "workspaces"}
            onClick={() => setTab("workspaces")}
          >
            Workspaces
          </TabButton>
        </div>

        {tab === "people" ? (
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>People</CardTitle>
                <CardDescription>
                  {people.length} {people.length === 1 ? "person" : "people"}{" "}
                  with access. Organization members see every facility; scoped
                  people see only what they're granted.
                </CardDescription>
              </div>
              <Button onClick={() => setAddOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" /> Add people
              </Button>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">Person</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead className="w-52 pr-6 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {people.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-muted-foreground py-8 pl-6 text-sm"
                      >
                        No one has access yet. Use “Add people” to invite staff
                        or grant a customer access to specific facilities.
                      </TableCell>
                    </TableRow>
                  ) : (
                    people.map((p) => {
                      const state = inviteState(p)
                      return (
                        <TableRow key={p.user_id} className="align-top">
                          <TableCell className="pl-6">
                            <div className="font-medium">
                              {p.name}
                              {p.user_id === current_user_id && (
                                <span className="text-muted-foreground ml-2 text-xs">
                                  (you)
                                </span>
                              )}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {p.email}
                            </div>
                            {state === "pending" && (
                              <Badge
                                variant="secondary"
                                className="mt-1 text-[10px]"
                              >
                                Pending invite
                              </Badge>
                            )}
                            {state === "expired" && (
                              <Badge
                                variant="destructive"
                                className="mt-1 text-[10px]"
                              >
                                Invite expired
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1.5">
                              {p.org_role && (
                                <Badge
                                  variant="secondary"
                                  className="capitalize"
                                >
                                  Organization · {p.org_role}
                                </Badge>
                              )}
                              {p.grants.map((g) => (
                                <Badge
                                  key={g.id}
                                  variant="outline"
                                  className="group gap-1 capitalize"
                                >
                                  {g.scope_label} · {g.role}
                                  <button
                                    type="button"
                                    onClick={() => revokeGrant(p, g)}
                                    aria-label={`Revoke ${g.scope_label}`}
                                    className="text-muted-foreground hover:text-destructive -mr-1 ml-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                              {!p.org_role && p.grants.length === 0 && (
                                <span className="text-muted-foreground text-xs">
                                  No active access
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="pr-6 text-right">
                            {/* ONE affordance per person — name, invite,
                                access, and removal all live in the manage
                                dialog. */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setManageUserId(p.user_id)}
                              aria-label={`Manage ${p.name}`}
                            >
                              <Settings2 className="mr-1.5 h-4 w-4" /> Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <WorkspacesPanel
            workspaces={workspaces}
            facilities={facilities}
            people={people}
            roles={roles}
          />
        )}
      </div>

      <AddPeopleDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        workspaces={workspaces}
        facilities={facilities}
        roles={roles}
        orgName={org.name}
      />

      <ManagePersonDialog
        key={managePerson?.user_id ?? "closed"}
        person={managePerson}
        open={managePerson !== null}
        onOpenChange={(o) => !o && setManageUserId(null)}
        facilities={facilities}
        workspaces={workspaces}
        roles={roles}
        featureCatalog={feature_catalog}
        orgName={org.name}
        currentUserId={current_user_id}
      />
    </AppLayout>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  )
}

// --- Add people (scope-first) ---------------------------------------------

function AddPeopleDialog({
  open,
  onOpenChange,
  workspaces,
  facilities,
  roles,
  orgName,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  workspaces: WorkspaceRow[]
  facilities: FacilityOption[]
  roles: string[]
  orgName: string
}) {
  const routes = useOrgRoutes()
  const [level, setLevel] = useState<"org" | "scoped">("org")
  // target encodes "workspace:ID" | "facility:ID" for the scoped path.
  const form = useForm<{
    name: string
    email: string
    role: string
    target: string
  }>({ name: "", email: "", role: "viewer", target: "" })

  function close() {
    form.reset()
    setLevel("org")
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (level === "org") {
      form.transform((d) => ({ name: d.name, email: d.email, role: d.role }))
      form.post(routes.membersPath(), {
        preserveScroll: true,
        onSuccess: close,
      })
    } else {
      const [scope_type, scope_id] = form.data.target.split(":")
      form.transform((d) => ({
        name: d.name,
        email: d.email,
        role: d.role,
        scope_type,
        scope_id,
      }))
      form.post(routes.accessGrantsPath(), {
        preserveScroll: true,
        onSuccess: close,
      })
    }
  }

  const scopedReady = level === "scoped" ? form.data.target !== "" : true

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close())}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add people</DialogTitle>
            <DialogDescription>
              Invite by email. New people get a link to set their password.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="person@example.com"
                  autoFocus
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="add-name">Name</Label>
                <Input
                  id="add-name"
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
            </div>
            {form.errors.email && (
              <p className="text-destructive text-sm">{form.errors.email}</p>
            )}

            <div className="flex flex-col gap-2">
              <Label>Access level</Label>
              <div className="grid grid-cols-1 gap-2">
                <LevelCard
                  active={level === "org"}
                  onClick={() => setLevel("org")}
                  icon={<ShieldCheck className="h-4 w-4" />}
                  title="Entire organization"
                  desc={`Access to every facility in ${orgName}, now and future. For your staff.`}
                />
                <LevelCard
                  active={level === "scoped"}
                  onClick={() => setLevel("scoped")}
                  icon={<KeyRound className="h-4 w-4" />}
                  title="Specific facility or workspace"
                  desc="Scoped access only. For customers, partners, or contractors."
                />
              </div>
            </div>

            {level === "scoped" && (
              <div className="flex flex-col gap-2">
                <Label>Grant access to</Label>
                <Select
                  value={form.data.target}
                  onValueChange={(v) => form.setData("target", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a workspace or facility" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Workspaces</SelectLabel>
                        {workspaces.map((w) => (
                          <SelectItem
                            key={`w${w.id}`}
                            value={`workspace:${w.id}`}
                          >
                            {w.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    <SelectGroup>
                      <SelectLabel>Facilities</SelectLabel>
                      {facilities.map((s) => (
                        <SelectItem key={`s${s.id}`} value={`facility:${s.id}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label>Role</Label>
              <Select
                value={form.data.role}
                onValueChange={(v) => form.setData("role", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r} className="capitalize">
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                Admins manage; editors operate on data; viewers are read-only.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={form.processing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={form.processing || !form.data.email || !scopedReady}
            >
              {form.processing
                ? "Adding…"
                : level === "org"
                  ? "Invite to organization"
                  : "Grant access"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LevelCard({
  active,
  onClick,
  icon,
  title,
  desc,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        active
          ? "border-primary bg-primary/5"
          : "border-input hover:bg-muted/50"
      }`}
    >
      <span
        className={`mt-0.5 ${active ? "text-primary" : "text-muted-foreground"}`}
      >
        {icon}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-muted-foreground text-xs">{desc}</span>
      </span>
    </button>
  )
}

// Compact inline role picker for changing an existing grant/membership role in
// place. Presentational — the caller owns the PATCH. Reused for org role,
// workspace grants, and facility grants.
function RoleSelect({
  value,
  roles,
  onChange,
  disabled,
  label,
}: {
  value: string
  roles: string[]
  onChange: (role: string) => void
  disabled?: boolean
  label: string
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className="h-7 w-[104px] text-xs capitalize"
        aria-label={label}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {roles.map((r) => (
          <SelectItem key={r} value={r} className="capitalize">
            {r}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// --- Per-person access (workspaces + facilities) --------------------------------

// Where a person's access to one facility comes from. Direct grants are removable
// here; org-wide and workspace-derived access are shown but locked (you'd
// remove the membership / workspace grant to change those).
type FacilityAccess =
  | { state: "none" }
  | { state: "direct"; grant: GrantRow }
  | { state: "org" }
  | { state: "workspace"; workspaceName: string }

function facilityAccessFor(
  person: Person,
  facilityId: number,
  workspaces: WorkspaceRow[],
): FacilityAccess {
  // Resolve to the broadest source first so the checkbox reflects what
  // unchecking would actually do. Org membership and workspace coverage grant
  // the facility regardless of any direct grant, so they win and lock the row — a
  // direct grant only reads as removable when it's the sole source of access.
  // (Redundant grants are still removable from the table's grant badges.)
  if (person.org_role) return { state: "org" }
  const ws = workspaces.find(
    (w) =>
      w.facility_ids.includes(facilityId) &&
      person.grants.some(
        (g) => g.scope_type === "workspace" && g.scope_id === w.id,
      ),
  )
  if (ws) return { state: "workspace", workspaceName: ws.name }
  const direct = person.grants.find(
    (g) => g.scope_type === "facility" && g.scope_id === facilityId,
  )
  if (direct) return { state: "direct", grant: direct }
  return { state: "none" }
}

// THE surface for one person — profile (name, invite), organization role,
// workspace/facility access, feature restrictions, and removal, in one dialog.
// Every mutation reuses the same endpoints the page already had (members /
// access_grants / access_users / access_invitations / access_entitlements),
// so all the admin guards hold; this is a consolidation, not a new API.
function ManagePersonDialog({
  person,
  open,
  onOpenChange,
  facilities,
  workspaces,
  roles,
  featureCatalog,
  orgName,
  currentUserId,
}: {
  person: Person | null
  open: boolean
  onOpenChange: (o: boolean) => void
  facilities: FacilityOption[]
  workspaces: WorkspaceRow[]
  roles: string[]
  featureCatalog: FeatureCatalogItem[]
  orgName: string
  currentUserId: number
}) {
  const routes = useOrgRoutes()
  const [role, setRole] = useState("viewer")
  const [filter, setFilter] = useState("")
  // "facility:ID" | "workspace:ID" | "feature:KEY" while a request is in flight.
  const [pending, setPending] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  // The dialog is keyed by user id, so this initializes per person.
  const nameForm = useForm<{ name: string }>({ name: person?.name ?? "" })

  if (!person) return null
  const state = inviteState(person)

  function saveName(e: React.FormEvent) {
    e.preventDefault()
    nameForm.patch(routes.accessUserPath(person!.user_id), {
      preserveScroll: true,
    })
  }

  function resendInvite() {
    if (resending) return
    if (!confirm(`Re-send the invitation email to ${person!.email}?`)) return
    setResending(true)
    router.post(
      routes.accessInvitationsPath(),
      { user_id: person!.user_id },
      { preserveScroll: true, onFinish: () => setResending(false) },
    )
  }

  function removeAccess() {
    const p = person!
    const done = {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    }
    if (p.org_membership_id) {
      const msg =
        p.user_id === currentUserId
          ? `Leave ${orgName}? You will lose organization-wide access.`
          : `Remove ${p.name}'s organization access? They keep any facility/workspace grants.`
      if (!confirm(msg)) return
      router.delete(routes.memberPath(p.org_membership_id), done)
    } else {
      // Scoped people have no membership to drop — "remove" revokes every
      // grant at once, after which they fall off the list entirely.
      const msg = `Remove all of ${p.name}'s access to ${orgName}? This revokes every facility and workspace grant they have.`
      if (!confirm(msg)) return
      router.delete(routes.removeAllAccessPath(p.user_id), done)
    }
  }

  // Deny-only feature-access toggle: when denied, POST a "deny" row; when
  // allowed, POST "default" to clear the row (inherit role). Runs through the
  // org-admin-guarded /access_entitlements endpoint. A person defaults to
  // allowed for every feature until denied here.
  const setFeatureAccess = (feature: string, denied: boolean) => {
    setPending(`feature:${feature}`)
    router.post(
      routes.accessEntitlementsPath(),
      { user_id: person.user_id, feature, effect: denied ? "deny" : "default" },
      { preserveScroll: true, onFinish: () => setPending(null) },
    )
  }

  const accessFor = (facilityId: number) =>
    facilityAccessFor(person, facilityId, workspaces)
  const accessibleCount = facilities.filter(
    (s) => accessFor(s.id).state !== "none",
  ).length
  const filtered = facilities.filter((s) =>
    s.name.toLowerCase().includes(filter.trim().toLowerCase()),
  )
  const workspaceGrantFor = (ws: WorkspaceRow) =>
    person.grants.find(
      (g) => g.scope_type === "workspace" && g.scope_id === ws.id,
    )

  const toggleGrant = (
    pendingKey: string,
    scopeType: "workspace" | "facility",
    scopeId: number,
    existingGrantId: number | undefined,
  ) => {
    setPending(pendingKey)
    const done = { preserveScroll: true, onFinish: () => setPending(null) }
    if (existingGrantId) {
      router.delete(routes.accessGrantPath(existingGrantId), done)
    } else {
      router.post(
        routes.accessGrantsPath(),
        {
          email: person.email,
          scope_type: scopeType,
          scope_id: scopeId,
          role,
        },
        done,
      )
    }
  }

  // Change an existing grant's role in place (promote/demote) — PATCHes the
  // same /access_grants endpoint the toggles use, so it runs through the same
  // org-admin guards.
  const changeGrantRole = (grantId: number, newRole: string) => {
    router.patch(
      routes.accessGrantPath(grantId),
      { role: newRole },
      { preserveScroll: true },
    )
  }

  // Change the person's organization-wide role. The backend rejects demoting
  // the last admin and flashes an alert (fail-visible), matching removeMember.
  const changeOrgRole = (newRole: string) => {
    if (!person.org_membership_id || newRole === person.org_role) return
    router.patch(
      routes.memberPath(person.org_membership_id),
      { role: newRole },
      { preserveScroll: true },
    )
  }

  const toggleWorkspace = (ws: WorkspaceRow) =>
    toggleGrant(
      `workspace:${ws.id}`,
      "workspace",
      ws.id,
      workspaceGrantFor(ws)?.id,
    )

  const toggleFacility = (facility: FacilityOption) => {
    const access = accessFor(facility.id)
    if (access.state === "org" || access.state === "workspace") return
    toggleGrant(
      `facility:${facility.id}`,
      "facility",
      facility.id,
      access.state === "direct" ? access.grant.id : undefined,
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-4 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{person.name}</DialogTitle>
          <DialogDescription>
            {person.email} · can access {accessibleCount} of {facilities.length}{" "}
            {facilities.length === 1 ? "facility" : "facilities"}
          </DialogDescription>
        </DialogHeader>

        {/* Profile: display name + invite state, inline. */}
        <form onSubmit={saveName} className="flex flex-col gap-1.5">
          <Label
            htmlFor="person-name"
            className="text-muted-foreground text-xs font-medium uppercase"
          >
            Name
          </Label>
          <div className="flex gap-2">
            <Input
              id="person-name"
              value={nameForm.data.name}
              onChange={(e) => nameForm.setData("name", e.target.value)}
              placeholder="Jane Doe"
              required
            />
            {nameForm.data.name.trim() !== person.name && (
              <Button
                type="submit"
                disabled={nameForm.processing || !nameForm.data.name.trim()}
              >
                {nameForm.processing ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
          {nameForm.errors.name && (
            <p className="text-destructive text-sm">{nameForm.errors.name}</p>
          )}
        </form>

        {state !== "accepted" && (
          <div className="bg-muted/50 flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
            <span className="text-muted-foreground">
              {state === "expired"
                ? "Their invitation expired before they signed in."
                : "They haven't accepted their invitation yet."}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={resendInvite}
              disabled={resending}
            >
              <Mail className="mr-1.5 h-4 w-4" />
              {resending ? "Sending…" : "Resend invite"}
            </Button>
          </div>
        )}

        {person.org_role ? (
          <div className="bg-muted/50 rounded-md border p-3 text-sm">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <span className="text-foreground font-medium">
                Organization role
              </span>
              <RoleSelect
                value={person.org_role}
                roles={roles}
                onChange={changeOrgRole}
                label={`Organization role for ${person.name}`}
              />
            </div>
            <p className="text-muted-foreground">
              Access to every facility, now and future. Remove their
              organization membership to scope them to specific facilities or
              workspaces.
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">
              Add new access as
            </span>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!person.org_role && workspaces.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-muted-foreground text-xs font-medium uppercase">
              Workspaces
            </Label>
            <div className="flex max-h-40 shrink-0 flex-col gap-0.5 overflow-y-auto rounded-md border p-2">
              {workspaces.map((ws) => {
                const grant = workspaceGrantFor(ws)
                return (
                  <label
                    key={ws.id}
                    className="hover:bg-muted/50 flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <Checkbox
                        aria-label={ws.name}
                        checked={!!grant}
                        disabled={pending === `workspace:${ws.id}`}
                        onCheckedChange={() => toggleWorkspace(ws)}
                      />
                      {ws.name}
                      <span className="text-muted-foreground text-xs">
                        {ws.facility_ids.length}{" "}
                        {ws.facility_ids.length === 1
                          ? "facility"
                          : "facilities"}
                      </span>
                    </span>
                    {grant && (
                      <RoleSelect
                        value={grant.role}
                        roles={roles}
                        onChange={(r) => changeGrantRole(grant.id, r)}
                        disabled={pending === `workspace:${ws.id}`}
                        label={`Role for ${ws.name}`}
                      />
                    )}
                  </label>
                )
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          {!person.org_role && workspaces.length > 0 && (
            <Label className="text-muted-foreground text-xs font-medium uppercase">
              Facilities
            </Label>
          )}
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter facilities…"
              className="pl-8"
            />
          </div>
        </div>

        <div className="flex max-h-72 shrink-0 flex-col gap-0.5 overflow-y-auto rounded-md border p-2">
          {filtered.length === 0 ? (
            <p className="text-muted-foreground p-2 text-sm">
              {facilities.length === 0
                ? "No facilities in this organization yet."
                : "No facilities match your filter."}
            </p>
          ) : (
            filtered.map((s) => {
              const access = accessFor(s.id)
              const checked = access.state !== "none"
              const locked =
                access.state === "org" || access.state === "workspace"
              return (
                <label
                  key={s.id}
                  className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm ${
                    locked ? "" : "hover:bg-muted/50 cursor-pointer"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Checkbox
                      aria-label={s.name}
                      checked={checked}
                      disabled={locked || pending === `facility:${s.id}`}
                      onCheckedChange={() => toggleFacility(s)}
                    />
                    {s.name}
                  </span>
                  {access.state === "org" && (
                    <Badge variant="secondary" className="text-[10px]">
                      Organization
                    </Badge>
                  )}
                  {access.state === "workspace" && (
                    <Badge variant="secondary" className="text-[10px]">
                      via {access.workspaceName}
                    </Badge>
                  )}
                  {access.state === "direct" && (
                    <RoleSelect
                      value={access.grant.role}
                      roles={roles}
                      onChange={(r) => changeGrantRole(access.grant.id, r)}
                      disabled={pending === `facility:${s.id}`}
                      label={`Role for ${s.name}`}
                    />
                  )}
                </label>
              )
            })
          )}
        </div>

        {featureCatalog.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-muted-foreground text-xs font-medium uppercase">
              Feature access
            </Label>
            <div className="flex shrink-0 flex-col gap-0.5 rounded-md border p-2">
              {featureCatalog.map((f) => {
                const denied = person.feature_access?.[f.key] === "deny"
                return (
                  <label
                    key={f.key}
                    className="hover:bg-muted/50 flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <span className="flex flex-col">
                      <span>{f.label}</span>
                      <span className="text-muted-foreground text-xs">
                        {denied
                          ? "Hidden from this person"
                          : "Visible (inherits role)"}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Restrict
                      </span>
                      <Checkbox
                        aria-label={`Restrict ${f.label}`}
                        checked={denied}
                        disabled={pending === `feature:${f.key}`}
                        onCheckedChange={(c) =>
                          setFeatureAccess(f.key, c === true)
                        }
                      />
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={removeAccess}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-4 w-4" />
            {person.org_membership_id
              ? "Remove from organization"
              : "Remove all access"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Workspaces tab (define facility-groups) ----------------------------------

function WorkspacesPanel({
  workspaces,
  facilities,
  people,
  roles,
}: {
  workspaces: WorkspaceRow[]
  facilities: FacilityOption[]
  people: Person[]
  roles: string[]
}) {
  const routes = useOrgRoutes()
  const [createOpen, setCreateOpen] = useState(false)
  // Track by id and re-derive from the live prop so the manage dialog
  // reflects membership changes while it's open (same pattern as
  // managePerson above).
  const [manageWsId, setManageWsId] = useState<number | null>(null)
  const manageWs = workspaces.find((w) => w.id === manageWsId) ?? null
  const wsForm = useForm<{ name: string; facility_ids: number[] }>({
    name: "",
    facility_ids: [],
  })

  function openCreate() {
    wsForm.setData({ name: "", facility_ids: [] })
    setCreateOpen(true)
  }

  function toggleFacility(id: number) {
    const next = wsForm.data.facility_ids.includes(id)
      ? wsForm.data.facility_ids.filter((s) => s !== id)
      : [...wsForm.data.facility_ids, id]
    wsForm.setData("facility_ids", next)
  }

  function submitCreate(e: React.FormEvent) {
    e.preventDefault()
    wsForm.post(routes.workspacesPath(), {
      preserveScroll: true,
      onSuccess: () => {
        wsForm.reset()
        setCreateOpen(false)
      },
    })
  }

  const facilityName = (id: number) =>
    facilities.find((s) => s.id === id)?.name ?? `#${id}`

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5" /> Workspaces
            </CardTitle>
            <CardDescription>
              Group facilities so you can grant a customer access to several at
              once. Click a workspace's people count to see and manage its
              members.
            </CardDescription>
          </div>
          <Button onClick={openCreate}>New workspace</Button>
        </CardHeader>
        <CardContent>
          {workspaces.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No workspaces yet. Group facilities (e.g. “Pilots”, “Customers”)
              to grant access in bulk.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Facilities</TableHead>
                  <TableHead>People</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaces.map((ws) => (
                  <TableRow key={ws.id}>
                    <TableCell className="font-medium">{ws.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {ws.facility_ids.length === 0
                        ? "—"
                        : ws.facility_ids.map(facilityName).join(", ")}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => setManageWsId(ws.id)}
                        aria-label={`View ${ws.name}'s members`}
                        className="text-foreground underline-offset-2 hover:underline"
                      >
                        {ws.members.length}{" "}
                        {ws.members.length === 1 ? "person" : "people"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      {/* ONE affordance per workspace — name, facilities, members,
                          and deletion all live in the manage dialog. */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setManageWsId(ws.id)}
                        aria-label={`Manage ${ws.name}`}
                      >
                        <Settings2 className="mr-1.5 h-4 w-4" /> Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <form onSubmit={submitCreate}>
            <DialogHeader>
              <DialogTitle>New workspace</DialogTitle>
              <DialogDescription>
                Name the workspace and choose which facilities belong to it.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="ws-name">Name</Label>
                <Input
                  id="ws-name"
                  value={wsForm.data.name}
                  onChange={(e) => wsForm.setData("name", e.target.value)}
                  placeholder="Pilots"
                  autoFocus
                />
                {wsForm.errors.name && (
                  <p className="text-destructive text-sm">
                    {wsForm.errors.name}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label>Facilities</Label>
                <div className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-md border p-3">
                  {facilities.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No facilities in this organization yet.
                    </p>
                  ) : (
                    facilities.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={wsForm.data.facility_ids.includes(s.id)}
                          onCheckedChange={() => toggleFacility(s.id)}
                        />
                        {s.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={wsForm.processing}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ManageWorkspaceDialog
        key={manageWs?.id ?? "manage-ws-closed"}
        workspace={manageWs}
        open={manageWs !== null}
        onOpenChange={(o) => !o && setManageWsId(null)}
        facilities={facilities}
        people={people}
        roles={roles}
      />
    </>
  )
}

// --- Manage one workspace ---------------------------------------------------

// THE surface for one workspace — details (name + facilities), members (grants on
// this workspace), and deletion, in one dialog. Mirrors ManagePersonDialog so
// both tabs manage their rows the same way; every mutation reuses the
// existing /workspaces and /access_grants endpoints.
function ManageWorkspaceDialog({
  workspace,
  open,
  onOpenChange,
  facilities,
  people,
  roles,
}: {
  workspace: WorkspaceRow | null
  open: boolean
  onOpenChange: (o: boolean) => void
  facilities: FacilityOption[]
  people: Person[]
  roles: string[]
}) {
  const routes = useOrgRoutes()
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [role, setRole] = useState("viewer")
  const [adding, setAdding] = useState(false)
  // Keyed by workspace id, so this initializes per workspace.
  const detailsForm = useForm<{ name: string; facility_ids: number[] }>({
    name: workspace?.name ?? "",
    facility_ids: workspace?.facility_ids ?? [],
  })

  if (!workspace) return null
  const ws = workspace

  const detailsDirty =
    detailsForm.data.name.trim() !== ws.name ||
    detailsForm.data.facility_ids.length !== ws.facility_ids.length ||
    detailsForm.data.facility_ids.some((id) => !ws.facility_ids.includes(id))

  function toggleDetailFacility(id: number) {
    const next = detailsForm.data.facility_ids.includes(id)
      ? detailsForm.data.facility_ids.filter((s) => s !== id)
      : [...detailsForm.data.facility_ids, id]
    detailsForm.setData("facility_ids", next)
  }

  function saveDetails(e: React.FormEvent) {
    e.preventDefault()
    detailsForm.put(routes.workspacePath(ws.id), { preserveScroll: true })
  }

  function deleteWorkspace() {
    if (
      !confirm(
        `Delete workspace "${ws.name}"? People lose this grouped access.`,
      )
    )
      return
    router.delete(routes.workspacePath(ws.id), {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  // Suggest existing people who don't already hold a grant here — typing a
  // brand-new email still works (it invites them, scoped to this workspace).
  const memberEmails = new Set(ws.members.map((m) => m.email))
  const suggestions = people.filter((p) => !memberEmails.has(p.email))
  const knownEmail = people.some((p) => p.email === email.trim().toLowerCase())

  function addMember(e: React.FormEvent) {
    e.preventDefault()
    setAdding(true)
    router.post(
      routes.accessGrantsPath(),
      {
        email,
        name,
        role,
        scope_type: "workspace",
        scope_id: ws.id,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEmail("")
          setName("")
        },
        onFinish: () => setAdding(false),
      },
    )
  }

  function revoke(m: WorkspaceMember) {
    if (!confirm(`Remove ${m.email} from ${ws.name}?`)) return
    router.delete(routes.accessGrantPath(m.grant_id), { preserveScroll: true })
  }

  // Change a workspace member's role in place via the same /access_grants
  // endpoint as the rest of the page.
  function changeRole(m: WorkspaceMember, newRole: string) {
    if (newRole === m.role) return
    router.patch(
      routes.accessGrantPath(m.grant_id),
      { role: newRole },
      { preserveScroll: true },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-4 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ws.name}</DialogTitle>
          <DialogDescription>
            {ws.facility_ids.length}{" "}
            {ws.facility_ids.length === 1 ? "facility" : "facilities"} ·{" "}
            {ws.members.length} {ws.members.length === 1 ? "person" : "people"}.
            Organization members can see every facility and aren't listed here.
          </DialogDescription>
        </DialogHeader>

        {/* Details: name + which facilities belong to this workspace. */}
        <form onSubmit={saveDetails} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="manage-ws-name"
              className="text-muted-foreground text-xs font-medium uppercase"
            >
              Name
            </Label>
            <Input
              id="manage-ws-name"
              value={detailsForm.data.name}
              onChange={(e) => detailsForm.setData("name", e.target.value)}
              placeholder="Pilots"
              required
            />
            {detailsForm.errors.name && (
              <p className="text-destructive text-sm">
                {detailsForm.errors.name}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-muted-foreground text-xs font-medium uppercase">
              Facilities
            </Label>
            <div className="flex max-h-40 shrink-0 flex-col gap-2 overflow-y-auto rounded-md border p-3">
              {facilities.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No facilities in this organization yet.
                </p>
              ) : (
                facilities.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={detailsForm.data.facility_ids.includes(s.id)}
                      onCheckedChange={() => toggleDetailFacility(s.id)}
                    />
                    {s.name}
                  </label>
                ))
              )}
            </div>
          </div>
          {detailsDirty && (
            <Button
              type="submit"
              disabled={detailsForm.processing || !detailsForm.data.name.trim()}
              className="self-end"
            >
              {detailsForm.processing ? "Saving…" : "Save changes"}
            </Button>
          )}
        </form>

        <Label className="text-muted-foreground -mb-2 text-xs font-medium uppercase">
          Members
        </Label>
        <div className="flex max-h-72 shrink-0 flex-col gap-0.5 overflow-y-auto rounded-md border p-2">
          {ws.members.length === 0 ? (
            <p className="text-muted-foreground p-2 text-sm">
              No one has been granted this workspace yet. Add someone below.
            </p>
          ) : (
            ws.members.map((m) => (
              <div
                key={m.grant_id}
                className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{m.name}</div>
                  <div className="text-muted-foreground truncate text-xs">
                    {m.email}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <RoleSelect
                    value={m.role}
                    roles={roles}
                    onChange={(r) => changeRole(m, r)}
                    label={`Role for ${m.name} in ${ws.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => revoke(m)}
                    aria-label={`Remove ${m.name} from ${ws.name}`}
                    className="text-muted-foreground hover:text-destructive p-1"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={addMember} className="flex flex-col gap-2">
          <Label htmlFor="ws-member-email">Add person to this workspace</Label>
          <div className="flex gap-2">
            <Input
              id="ws-member-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@example.com"
              list="ws-member-suggestions"
              className="flex-1"
              required
            />
            <datalist id="ws-member-suggestions">
              {suggestions.map((p) => (
                <option key={p.user_id} value={p.email}>
                  {p.name}
                </option>
              ))}
            </datalist>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" disabled={adding || !email.trim()}>
              {adding ? "Adding…" : "Add"}
            </Button>
          </div>
          {email.trim() !== "" && !knownEmail && (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (required for new people — they get an invite)"
              aria-label="Name for new person"
            />
          )}
        </form>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={deleteWorkspace}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete workspace
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
